import { ReactNode, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useViewMode } from '../contexts/ViewModeContext';

export interface Column<T> {
  key: string;
  header: string;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  hideOnMobile?: boolean;
  render?: (item: T, index: number) => ReactNode;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyExtractor: (item: T) => string;
  onRowClick?: (item: T) => void;
  rowClassName?: (item: T) => string;
  emptyMessage?: string;
  emptyIcon?: ReactNode;
  isLoading?: boolean;
  mobileCardRender?: (item: T, index: number) => ReactNode;
}

type SortDirection = 'asc' | 'desc' | null;

export function DataTable<T>({
  data,
  columns,
  keyExtractor,
  onRowClick,
  rowClassName,
  emptyMessage = 'Nessun dato trovato',
  emptyIcon,
  isLoading,
  mobileCardRender
}: DataTableProps<T>) {
  const { tableDensity, isMobile } = useViewMode();
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDirection>(null);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc');
      else if (sortDir === 'desc') { setSortKey(null); setSortDir(null); }
      else setSortDir('asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (!sortKey || !sortDir) return 0;
    const aVal = (a as any)[sortKey];
    const bVal = (b as any)[sortKey];
    if (aVal === bVal) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp = aVal < bVal ? -1 : 1;
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const densityClass = tableDensity === 'dense' ? 'dense' : tableDensity === 'relaxed' ? 'relaxed' : '';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="empty-state">
        {emptyIcon && <div className="empty-state-icon">{emptyIcon}</div>}
        <p className="empty-state-title">{emptyMessage}</p>
      </div>
    );
  }

  // Mobile card view
  if (isMobile && mobileCardRender) {
    return (
      <div className="mobile-card-list">
        {sortedData.map((item, index) => (
          <div
            key={keyExtractor(item)}
            onClick={() => onRowClick?.(item)}
            className={`${onRowClick ? 'cursor-pointer' : ''} ${rowClassName?.(item) || ''}`}
          >
            {mobileCardRender(item, index)}
          </div>
        ))}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className="table-container">
      <table className={`data-table ${densityClass}`}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`
                  ${col.sortable ? 'sortable' : ''} 
                  ${col.hideOnMobile ? 'hidden sm:table-cell' : ''}
                  ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : ''}
                `}
                style={col.width ? { width: col.width } : undefined}
                onClick={col.sortable ? () => handleSort(col.key) : undefined}
              >
                <div className="flex items-center gap-1">
                  <span>{col.header}</span>
                  {col.sortable && (
                    <span className="text-gray-400">
                      {sortKey === col.key ? (
                        sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                      ) : (
                        <ChevronsUpDown className="w-3 h-3 opacity-50" />
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => (
            <tr
              key={keyExtractor(item)}
              className={`${onRowClick ? 'clickable' : ''} ${rowClassName?.(item) || ''}`}
              onClick={() => onRowClick?.(item)}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`
                    ${col.hideOnMobile ? 'hidden sm:table-cell' : ''}
                    ${col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : ''}
                  `}
                >
                  {col.render ? col.render(item, index) : (item as any)[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Helper component per le celle con badge di stato
interface StatusBadgeProps {
  status: string;
  config: Record<string, { label: string; className: string }>;
}

export function StatusBadge({ status, config }: StatusBadgeProps) {
  const c = config[status] || { label: status, className: 'badge-gray' };
  return <span className={`badge ${c.className}`}>{c.label}</span>;
}

// Helper component per priority dot
interface PriorityDotProps {
  priority: string;
  showLabel?: boolean;
}

export function PriorityDot({ priority, showLabel = false }: PriorityDotProps) {
  const config: Record<string, { label: string; dotClass: string }> = {
    urgent: { label: 'Urgente', dotClass: 'priority-urgent' },
    high: { label: 'Alta', dotClass: 'priority-high' },
    medium: { label: 'Media', dotClass: 'priority-medium' },
    low: { label: 'Bassa', dotClass: 'priority-low' }
  };
  const c = config[priority] || config.medium;
  
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`priority-dot ${c.dotClass}`} />
      {showLabel && <span className="text-xs text-gray-600">{c.label}</span>}
    </span>
  );
}

// Toolbar per filtri
interface ToolbarProps {
  children: ReactNode;
  className?: string;
}

export function Toolbar({ children, className = '' }: ToolbarProps) {
  return <div className={`toolbar ${className}`}>{children}</div>;
}

// Risultati count
interface ResultsCountProps {
  count: number;
  label?: string;
}

export function ResultsCount({ count, label = 'risultati' }: ResultsCountProps) {
  return (
    <span className="text-xs text-gray-500">
      {count} {label}
    </span>
  );
}
